import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, QrCode, Lightning, Repeat } from '@phosphor-icons/react';

/**
 * Native QR scanner modal using device camera.
 * Props: open: bool, onClose(): void, onDetect(decodedText): void
 */
export default function QRScanner({ open, onClose, onDetect }) {
  const containerId = 'mg-qr-reader';
  const scannerRef = useRef(null);
  const [err, setErr] = useState('');
  const [cameras, setCameras] = useState([]);
  const [activeCam, setActiveCam] = useState(null);
  const [torch, setTorch] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let mounted = true;
    setErr('');

    (async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (!mounted) return;
        if (!devices || devices.length === 0) {
          setErr('No camera found on this device.');
          return;
        }
        setCameras(devices);
        // Prefer rear camera
        const rear = devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
        setActiveCam(rear.id);
      } catch (e) {
        setErr(e?.message || 'Camera access denied');
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !activeCam) return undefined;

    const html5 = new Html5Qrcode(containerId, /* verbose= */ false);
    scannerRef.current = html5;
    let stopped = false;

    html5
      .start(
        activeCam,
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        (decodedText) => {
          if (stopped) return;
          stopped = true;
          html5.stop().then(() => html5.clear()).catch(() => {});
          onDetect(decodedText);
        },
        () => { /* scan tick — ignore */ }
      )
      .catch((e) => setErr(e?.message || 'Failed to start camera'));

    return () => {
      stopped = true;
      if (html5 && html5.isScanning) {
        html5.stop().then(() => html5.clear()).catch(() => {});
      }
    };
  }, [activeCam, open, onDetect]);

  const toggleTorch = async () => {
    try {
      const html5 = scannerRef.current;
      if (!html5) return;
      const next = !torch;
      await html5.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorch(next);
    } catch (e) {
      setErr('Torch not supported on this device');
    }
  };

  const switchCam = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === activeCam);
    const next = cameras[(idx + 1) % cameras.length];
    if (scannerRef.current?.isScanning) {
      scannerRef.current.stop().then(() => setActiveCam(next.id)).catch(() => setActiveCam(next.id));
    } else {
      setActiveCam(next.id);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-carbon flex flex-col" data-testid="qr-scanner-modal">
      <header className="flex items-center justify-between p-4 border-b-2 border-canvas">
        <div className="flex items-center gap-2 text-canvas">
          <QrCode size={22} weight="bold" />
          <span className="font-display font-bold uppercase tracking-widest text-sm">Scan QR tag</span>
        </div>
        <button
          onClick={onClose}
          className="w-12 h-12 inline-flex items-center justify-center text-canvas border-2 border-canvas"
          data-testid="qr-close-btn"
          aria-label="Close scanner"
        >
          <X size={22} weight="bold" />
        </button>
      </header>

      <div className="flex-1 relative">
        <div id={containerId} className="w-full h-full" style={{ minHeight: '320px' }} />
        {/* Crosshair overlay */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-60 h-60 border-2 border-canvas" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }}>
            <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-brand-primary" />
            <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-brand-primary" />
            <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-brand-primary" />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-brand-primary" />
          </div>
        </div>
        {err && (
          <div className="absolute bottom-4 left-4 right-4 border-2 border-status-severe bg-canvas px-3 py-2 text-sm text-status-severe"
               data-testid="qr-error">
            {err}
          </div>
        )}
      </div>

      <footer className="p-4 grid grid-cols-2 gap-3 border-t-2 border-canvas">
        <button
          onClick={toggleTorch}
          className="min-h-[48px] border-2 border-canvas text-canvas inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-widest text-sm"
          data-testid="qr-torch-btn"
        >
          <Lightning size={18} weight={torch ? 'fill' : 'bold'} /> Torch
        </button>
        <button
          onClick={switchCam}
          disabled={cameras.length < 2}
          className="min-h-[48px] border-2 border-canvas text-canvas inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-widest text-sm disabled:opacity-40"
          data-testid="qr-switch-btn"
        >
          <Repeat size={18} weight="bold" /> Switch
        </button>
      </footer>
    </div>
  );
}
